import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import { type ComponentProps, type FC, useEffect } from 'react';

import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';

import { VerifiedCredentials } from '../Profile/VerifiedCredentials';

import translations from './.vocab';
import { VerificationsAlert } from './VerificationsAlert';
import { type Verification, VerificationsItem } from './VerificationsItem';

interface Props {
  onClickVerificationsAlertLearnMore: () => void;
  onCloseVerificationsAlert: () => void;
  onClickVerificationsTabFooterLearnMore: () => void;
  verifications: {
    verifiedIdentity?: Verification | null;
    workHistory: Verification[];
    licences: Verification[];
    education: Verification[];
    rightToWork: Verification[];
  };
  careerHistoryNotice: ComponentProps<typeof VerificationsItem>['notice'];
}

export const VerificationsResultState: FC<Props> = ({
  onClickVerificationsAlertLearnMore,
  onCloseVerificationsAlert,
  onClickVerificationsTabFooterLearnMore,
  verifications: {
    verifiedIdentity,
    workHistory,
    licences,
    education,
    rightToWork,
  },
  careerHistoryNotice,
}) => {
  const { t } = useTranslations(translations);

  const { getVerificationsTabNewBadgeStatus, hideVerificationsTabNewBadge } =
    useUserActionStatus('verifications-tab-new-badge');

  useEffect(() => {
    if (getVerificationsTabNewBadgeStatus() === 'visible') {
      hideVerificationsTabNewBadge();
    }
  }, [getVerificationsTabNewBadgeStatus, hideVerificationsTabNewBadge]);

  return (
    <Box data={{ testid: 'verifications-result' }} style={{ maxWidth: 710 }}>
      <Stack space="large">
        <VerificationsAlert
          onClose={onCloseVerificationsAlert}
          onClickLearnMore={onClickVerificationsAlertLearnMore}
        />
        <VerificationsItem
          heading={t('Digital ID')}
          tooltip={t(
            'A verified identity means this person has confirmed their identity with the SEEK Pass verification platform using a government-issued document.',
          )}
          verifications={
            verifiedIdentity ? [{ ...verifiedIdentity, title: '' }] : []
          }
        />
        <VerificationsItem
          heading={t('Career history')}
          notice={careerHistoryNotice}
          verifications={workHistory}
        />
        <VerificationsItem heading={t('Education')} verifications={education} />
        <VerificationsItem
          heading={t('Licences and certifications')}
          verifications={licences}
        />
        <VerificationsItem
          heading={t('Right to work')}
          verifications={rightToWork}
        />
        <VerifiedCredentials
          onClickLearnHowCredentialsAreVerified={
            onClickVerificationsTabFooterLearnMore
          }
        />
      </Stack>
    </Box>
  );
};
